import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable, { CellHookData } from 'jspdf-autotable';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/nl';
import 'dayjs/locale/de';
import 'dayjs/locale/fr';
import 'dayjs/locale/es';
import { TranslateService } from '@ngx-translate/core';
import { LocationType, OperatorSettingsManifest, PaymentType, TenantCurrency } from '../graphql/generated/gen-types';
import { TranslateCustomService } from '../utility/translate-custom.service';

import { addMadouBetaalOverzicht } from './manifest-madou';
import { addMontgolfiereBetaalOverzicht } from './manifest-montgolfiere';
import { addBallonschateaudoexBetaalOverzicht } from './manifest-ballonschateaudoex';
import { TenantSettingService } from '../services/tenantSetting/tenant-setting.service';
import { addAdballonDetailPassagiers } from './manifest-adballon';
import { addWestwindPassengierEinweisung } from './manifest-westwind';

@Injectable({
	providedIn: 'root',
})
export class ManifestService {
	domain: string;
	margin_top = 15;
	margin_left = 15;
	column_width = 85;
	margin_blocks = 3;
	fillinStyle = null;
	montgolfiere = false;
	madou = false;
	stepin = false;
	culiair = false;
	ballonschateaudoex = false;
	adballon = false;
	westwind = false;

	constructor(
		private translateCustom: TranslateCustomService,
		private tenantSettingService: TenantSettingService,
		private translate: TranslateService,
	) {}

	async generatePDF(
		tenant,
		flight,
		manifestOptions?: OperatorSettingsManifest,
		currency?: TenantCurrency,
		checkinEnabled: boolean = false
	) {
		this.stepin = tenant.includes('stepin');
		this.montgolfiere = tenant.includes('montgolfiere');
		this.madou = tenant.includes('madou');
		this.ballonschateaudoex = tenant.includes('ballonschateaudoex');
		this.culiair = tenant.includes('culiair');
		this.adballon = tenant.includes('Ad Ballon');
		this.westwind = tenant.toLowerCase().includes('westwind');

		if (this.montgolfiere) {
			this.fillinStyle = { lineColor: 'green', minCellWidth: 12 };
		}

		const Yfuel = 111;
		const Yfooter = 220;
		const font = 'Helvetica';
		const fontSizeTitle = 12;
		const fontSizeFootnote = 6;
		const document = this.mapFlightManifest(flight);
		const title = dayjs(flight.date).format('YYYYMMDD') + '_' + flight.period + '_' + document.data.header.balloon + '.pdf';
		const doc = new jsPDF(),
			margins = {
				top: this.margin_top,
				left: this.margin_left,
			};
		// Title
		doc.setFontSize(14);
		doc.text('OPERATIONAL FLIGHT PLAN', 105, this.margin_top, {
			align: 'center',
		});
		// Header
		const headerBody = [
			[
				document.labels.header.operator,
				{
					content: tenant.toUpperCase(),
					colSpan: 4,
				},
				'', // document.labels.header.docsn,
				'', // document.data.header.docsn,
			],
			[
				document.labels.header.balloon,
				{
					content: document.data.header.balloon,
					colSpan: 2,
				},
				document.labels.header.balloonType,
				document.data.header.balloonType,
			],
			[
				document.labels.header.pic,
				{
					content: document.data.header.pic,
					colSpan: 2,
				},
				document.labels.header.date,
				document.data.header.date,
			],

			[
				document.labels.header.takeoffLocation,
				{
					content: document.data.header.takeoffLocation,
					colSpan: 2,
				},
				document.labels.header.landingLocation,
				{
					content: document.data.header.landingLocation,
					styles: document.data.header.landingLocation === '' && this.montgolfiere ? { lineColor: 'green', minCellWidth: 12 } : null,
				},
			],
			[
				document.labels.header.takeoffTime,
				document.data.header.flightHour,
				{
					content: document.data.header.takeoffTime,
					styles: this.fillinStyle,
				},
				document.labels.header.landingTime,
				{
					content: document.data.header.landingTime,
					styles: document.data.header.landingTime === '' && this.montgolfiere ? { lineColor: 'green', minCellWidth: 12 } : null,
				},
			],
		];
		// Flight crew ico Ballonschateauxdoex
		if (this.ballonschateaudoex) {
			headerBody.push([
				document.labels.header.crew,
				{
					content: document.data.header.crew,
					colSpan: 4,
				},
			]);
		}

		// Flight comment ico Madou
		if (this.madou && document.data.header.flightComments) {
			headerBody.push([
				{
					content: document.data.header.flightComments,
					colSpan: 5,
					styles: { fontSize: 8 },
				},
			]);
		}

		// Flight comment ico Madou
		if (this.madou && flight.locationType === LocationType.Priv) {
			headerBody.push([
				{
					content: `${document.data.header.takeoffLocation}: ${document.data.header.takeoffAddress}`,
					colSpan: 5,
					styles: { fontSize: 8 },
				},
			]);
		}

		autoTable(doc, {
			head: [
				[
					{
						content: document.labels.header.load_chart_and_passenger_list,
						colSpan: 5,
					},
				],
			],
			body: headerBody,
			startY: this.margin_top + 3,
			theme: 'grid',
			styles: { halign: 'left', valign: 'middle', fontSize: 9, cellPadding: 1 },
			margin: { left: this.margin_left },
			tableWidth: 2 * this.column_width + this.margin_blocks,
			headStyles: {
				fillColor: '#d3d3d3',
				textColor: '#000000',
				fontSize: 14,
				halign: 'center',
			},
		});
		const Yheader = (doc as any).lastAutoTable.finalY;

		// Passenger List
		const passengers = [];
		let disabilityCount = 0;
		let childCount = 0;
		let transportCount = 0;
		let checkinCount = 0;
		if (!this.madou) {
			document.data.passengers.forEach((p) => {
				const paxName = p.age ? p.name + ' ' + `(${p.age}y)` : p.name;
				if (p.child) childCount += 1;
				if (p.disability) disabilityCount += 1;
				if (p.transport) transportCount += 1;
				if (p.onlineCheckinAt) checkinCount += 1;
				const row: any[] = [
					{ content: p.id, styles: { fontSize: 8, halign: 'center' } },
					{
						content: paxName,
						styles: { fontSize: 8 },
					},
					{ content: `${p.weight} (+7kg)`, styles: { fontSize: 8, halign: 'center' } },

					{
						content: p.child ? 'C' : '',
						styles: { fontSize: 8, fontStyle: p.child ? 'bold' : 'normal', halign: 'center' },
					},
					{
						content: p.disability ? 'A' : '',
						styles: { fontSize: 8, fontStyle: p.disability ? 'bold' : 'normal', halign: 'center' },
					},
					{
						content: p.transport ? 'T' : '',
						styles: { fontSize: 8, fontStyle: p.transport ? 'bold' : 'normal', halign: 'center' },
					},
				];
				if (checkinEnabled) {
					row.push({
						content: p.onlineCheckinAt ? '✓' : '',
						styles: { fontSize: 8, halign: 'center' },
					});
				}
				passengers.push(row);
			});
		} else {
			document.data.passengers.forEach((p) => {
				let category = '';
				if (p.category) category = p.category.name;
				if (p.child) category = '(C) ' + category;
				if (p.disability) category = '(A) ' + category;
				if (p.transport) category = '(T) ' + category;
				if (p.onlineCheckinAt) checkinCount += 1;
				const row: any[] = [
					{ content: p.id, styles: { halign: 'center' } },
					{
						content: p.name,
						styles: { fontSize: 8 },
					},
					{ content: `${p.weight} (+7kg)`, styles: { fontSize: 8, halign: 'center' } },
					{
						content: category,
						styles: { fontSize: 8, halign: 'center' },
						colSpan: 3,
					},
				];
				if (checkinEnabled) {
					row.push({
						content: p.onlineCheckinAt ? '✓' : '',
						styles: { fontSize: 8, halign: 'center' },
					});
				}
				passengers.push(row);
			});
		}
		const totalsRow: any[] = [
			{
				content: document.data.header.pilot
					? `Totals (incl. PIC - ${document.data.header.pilot.crew.totalWeight} kg)`
					: 'Totals',
				colSpan: 2,
				styles: { halign: 'right', fontStyle: 'bold', cellPadding: 1 },
			},
			{
				content: document.data.passengers_total_weight + ' kg',
				colSpan: 1,
				styles: { halign: 'center', fontStyle: 'bold', cellPadding: 1 },
			},
			{
				content: childCount === 0 ? '' : `${childCount}C`,
				colSpan: 1,
				styles: { halign: 'center', fontStyle: 'bold', cellPadding: 1 },
			},
			{
				content: disabilityCount === 0 ? '' : `${disabilityCount}A`,
				colSpan: 1,
				styles: { halign: 'center', fontStyle: 'bold', cellPadding: 1 },
			},
			{
				content: transportCount === 0 ? '' : `${transportCount}T`,
				colSpan: 1,
				styles: { halign: 'center', fontStyle: 'bold', cellPadding: 1 },
			},
		];
		if (checkinEnabled) {
			totalsRow.push({
				content: checkinCount === 0 ? '' : `${checkinCount}`,
				colSpan: 1,
				styles: { halign: 'center', fontStyle: 'bold', cellPadding: 1 },
			});
		}
		passengers.push(totalsRow);
		let cellPaddingPassengers = 1;
		if (passengers.length > 19) cellPaddingPassengers = 0.2;
		autoTable(doc, {
			head: [[document.labels.header.passenger_list]],
			startY: Yheader + this.margin_blocks,
			theme: 'grid',
			margin: { left: this.margin_left },
			tableWidth: this.column_width,
			headStyles: {
				fillColor: '#d3d3d3',
				textColor: '#000000',
				fontSize: 12,
				halign: 'center',
			},
		});
		const headerRow: any[] = [
			'No',
			'NAME',
			'WEIGHT',
			{ content: '(C)', styles: { minCellWidth: 6, halign: 'center' } },
			{ content: '(A)', styles: { minCellWidth: 6, halign: 'center' } },
			{ content: '(T)', styles: { minCellWidth: 6, halign: 'center' } },
		];
		if (checkinEnabled) {
			headerRow.push({ content: '(CI)', styles: { minCellWidth: 6, halign: 'center' } });
		}
		autoTable(doc, {
			head: [headerRow],
			body: passengers,
			startY: Yheader + this.margin_blocks + 8,
			theme: 'grid',
			styles: {
				halign: 'left',
				valign: 'middle',
				fontSize: 9,
				cellPadding: cellPaddingPassengers,
			},
			margin: { left: this.margin_left },
			tableWidth: this.column_width,
			headStyles: { fillColor: '#d3d3d3', textColor: '#000000', fontSize: 9 },
		});
		const Ypassengers = (doc as any).lastAutoTable.finalY;
		doc.setFontSize(fontSizeFootnote);
		doc.text(
			checkinEnabled
				? '(C) CHILD, (A) ASSISTANCE, (T) TRANSPORT, (CI) CHECK-IN'
				: '(C) CHILD, (A) ASSISTANCE, (T) TRANSPORT',
			this.margin_left,
			Ypassengers + 3
		);

		// ALL - pilotDeclaration
		if (manifestOptions?.pilotDeclaration) {
			this.addPilotDeclaration(doc, manifestOptions);
		}

		// Title Weather List
		autoTable(doc, {
			head: [
				[
					{
						content: document.labels.header.weather_conditions,
						colSpan: 4,
					},
				],
			],
			body: [
				[document.labels.weather.source, document.data.weather.source, 'DATE', document.data.weather.date],
				[document.labels.weather.visual, document.data.weather.visual, document.labels.weather.clouds, document.data.weather.clouds],
				['TEMP', document.data.weather.temp, document.labels.weather.qfe, document.data.weather.qfe],
				[
					{
						content: document.data.weather.windSummary,
						colSpan: 4,
						styles: { fontSize: 8 },
					},
				],
			],
			startY: Yheader + this.margin_blocks,
			theme: 'grid',
			styles: { halign: 'left', valign: 'middle', fontSize: 9, cellPadding: 1 },
			margin: {
				left: this.margin_left + this.column_width + this.margin_blocks,
			},
			tableWidth: this.column_width,
			headStyles: {
				fillColor: '#d3d3d3',
				textColor: '#000000',
				fontSize: 12,
				halign: 'center',
			},
		});

		// Title Fuel Calculations
		autoTable(doc, {
			head: [
				[
					{
						content: document.labels.header.fuel_calculations,
						colSpan: 2,
					},
				],
			],
			body: [
				[document.labels.fuel.plannedTime, document.data.fuel.plannedTime],
				[document.labels.fuel.available, document.data.fuel.available],
				[document.labels.fuel.needed, document.data.fuel.needed],
				[document.labels.fuel.consumption, document.data.fuel.consumption],
			],
			startY: Yfuel,
			theme: 'grid',
			styles: { halign: 'left', valign: 'middle', fontSize: 9, cellPadding: 1 },
			margin: {
				left: this.margin_left + this.column_width + this.margin_blocks,
			},
			tableWidth: this.column_width,
			headStyles: {
				fillColor: '#d3d3d3',
				textColor: '#000000',
				fontSize: 12,
				halign: 'center',
			},
		});
		const Ylift = (doc as any).lastAutoTable.finalY;

		// Title Lift Calculations
		autoTable(doc, {
			head: [
				[
					{
						content: document.labels.header.load_chart,
						colSpan: 2,
					},
				],
			],
			body: [
				[document.labels.lift.temp, document.data.lift.temp],
				[document.labels.lift.pressure, document.data.lift.pressure],
				[document.labels.lift.takeoffElevation, document.data.lift.takeoffElevation],
				[document.labels.lift.maxElevation, document.data.lift.maxElevation],
				// [document.labels.lift.balloonTemp, document.data.lift.balloonTemp],
				[document.labels.lift.volume, document.data.lift.volume],
				[document.labels.lift.lift, document.data.lift.lift],
				[document.labels.lift.liftNeeded, document.data.lift.liftNeeded],
			],
			startY: Ylift,
			theme: 'grid',
			styles: { halign: 'left', valign: 'middle', fontSize: 9, cellPadding: 1 },
			margin: {
				left: this.margin_left + this.column_width + this.margin_blocks,
			},
			tableWidth: this.column_width,
			headStyles: {
				fillColor: '#d3d3d3',
				textColor: '#000000',
				fontSize: 12,
				halign: 'center',
			},
		});
		const Ylift1 = (doc as any).lastAutoTable.finalY;

		// Lift Calculations - asset weights
		autoTable(doc, {
			head: [],
			body: [
				[document.labels.lift.envelope, document.labels.lift.basket, document.labels.lift.burner, document.labels.lift.cylinders],
				[document.data.lift.envelope, document.data.lift.basket, document.data.lift.burner, document.data.lift.cylinders],
			],
			startY: Ylift1,
			theme: 'grid',
			styles: { halign: 'left', valign: 'middle', fontSize: 9, cellPadding: 1 },
			margin: {
				left: this.margin_left + this.column_width + this.margin_blocks,
			},
			tableWidth: this.column_width,
		});
		const Ylift2 = (doc as any).lastAutoTable.finalY;
		// Lift Calculations - signature
		autoTable(doc, {
			head: [],
			body: [
				['Last minute updates', document.labels.footer.signature],
				[
					{
						content: document.data.header.pilotComments,
						styles: { minCellHeight: 12 },
					},
					{
						content: '',
						styles: this.fillinStyle,
					},
				],
			],
			startY: Ylift2,
			theme: 'grid',
			styles: { halign: 'left', valign: 'middle', fontSize: 9, cellPadding: 1 },
			margin: {
				left: this.margin_left + this.column_width + this.margin_blocks,
			},
			tableWidth: this.column_width,
		});

		// footer
		const tableWidthfooter = 2 * this.column_width + this.margin_blocks;
		const cellWidthfooter = tableWidthfooter / 4;

		autoTable(doc, {
			head: [[{ content: document.labels.footer.after_flight, colSpan: 4 }]],
			body: [
				[
					document.labels.footer.visible_defects,
					{
						content: document.labels.footer.yes_no,
						styles: this.fillinStyle,
					},
					document.labels.footer.date,
					{
						content: '',
						styles: this.fillinStyle,
					},
				],
				[
					{ content: '', styles: { minCellHeight: 12 } },
					{ content: '' },
					{ content: document.labels.footer.signature },
					{
						content: '',
						styles: this.fillinStyle,
					},
				],
				[
					{ content: document.labels.footer.defect, colSpan: 2 },
					{ content: document.labels.footer.action, colSpan: 2 },
				],
				[
					{
						content: '',
						colSpan: 2,
						styles: this.fillinStyle,
					},
					{
						content: '',
						colSpan: 2,
						styles: this.fillinStyle,
					},
				],
			],
			startY: Yfooter,
			theme: 'grid',
			margin: { left: this.margin_left },
			styles: {
				halign: 'left',
				valign: 'middle',
				fontSize: 9,
				cellPadding: 1,
				cellWidth: cellWidthfooter,
			},
			tableWidth: tableWidthfooter,
			headStyles: {
				fillColor: '#d3d3d3',
				textColor: '#000000',
				fontSize: 12,
				halign: 'center',
			},
		});
		const Yfooter1 = (doc as any).lastAutoTable.finalY;
		const cellWidthfooter1 = tableWidthfooter / 5;
		autoTable(doc, {
			head: [],
			body: [
				[
					document.labels.footer.certificate,
					document.labels.footer.defect,
					document.labels.footer.authority,
					document.labels.footer.initials,
					document.labels.footer.date,
				],
				[{ content: '', styles: { minCellHeight: 12 } }],
			],
			startY: Yfooter1,
			theme: 'grid',
			margin: { left: this.margin_left },
			styles: {
				halign: 'left',
				valign: 'middle',
				fontSize: 9,
				cellPadding: 1,
				cellWidth: cellWidthfooter1,
			},
			tableWidth: tableWidthfooter,
		});
		const Yfooter2 = (doc as any).lastAutoTable.finalY;

		// ALL - footer
		if (manifestOptions?.footer) {
			this.addFooter(doc, manifestOptions, Yfooter2 + 3);
		}

		// MADOU betaaloverzicht
		if (this.madou)
			addMadouBetaalOverzicht(doc, document, {
				margin_top: this.margin_top,
				margin_left: this.margin_left,
				column_width: this.column_width,
				margin_blocks: this.margin_blocks,
			});

		// MONTGOLFIERE betaaloverzicht
		if (this.montgolfiere)
			addMontgolfiereBetaalOverzicht(doc, document, currency, {
				margin_left: this.margin_left,
				column_width: this.column_width,
				margin_blocks: this.margin_blocks,
			});

		// Ballonschateauxdoex betaaloverzicht
		if (this.ballonschateaudoex)
			addBallonschateaudoexBetaalOverzicht(doc, document, currency, {
				column_width: this.column_width,
				margin_blocks: this.margin_blocks,
				margin_top: this.margin_top,
				margin_left: this.margin_left,
			});

		// Adballon betaaloverzicht
		if (this.adballon)
			addAdballonDetailPassagiers(doc, document, currency, {
				column_width: this.column_width,
				margin_blocks: this.margin_blocks,
				margin_top: this.margin_top,
				margin_left: this.margin_left,
			});

		// Westwind Passagiereinweisung
		if (this.westwind)
			addWestwindPassengierEinweisung(doc, document, {
				column_width: this.column_width,
				margin_blocks: this.margin_blocks,
				margin_top: this.margin_top,
				margin_left: this.margin_left,
			});

		doc.setProperties({ title: title });
		window.open(doc.output('bloburl').toString());
		doc.save(title);
	}

	addPilotDeclaration(doc, manifestOptions) {
		const Ydeclaration = 200;
		doc.setFontSize(6);
		manifestOptions.pilotDeclaration.forEach((line, index) => {
			doc.text(line, this.margin_left, Ydeclaration + index * 3);
		});
	}
	addFooter(doc, manifestOptions, Y) {
		doc.setFontSize(6);
		manifestOptions.footer.forEach((line, index) => {
			doc.text(line, 105, Y + index * 3, { align: 'center' });
		});
	}

	mapFlightManifest(flight) {
		let manifestFlight: any;
		let callSign = '';
		let balloonType = '';
		let balloonName = '';
		let pic = '';
		let crew = '';
		let flightHour = '';
		let landingTime = '';
		let takeoffTime = '';
		let consumption = '______ L';
		let visual = flight.vfrVisual?.toString(10);
		let clouds = flight.vfrClouds?.toString(10);
		let windSummary = '';
		let i = 0;
		let passengersCount = 0;
		const passengers = [];
		const payments = [];
		const distinct = (value, index, self) => {
			return self.indexOf(value) === index;
		};

		if (flight.balloon) {
			callSign = flight.balloon.callSign;
			balloonType = flight.balloon.envelopeType;
			balloonName = flight.balloon.name;
		}
		if (clouds === '0') {
			clouds = 'CLEAR';
		}
		if (visual === '0') {
			visual = 'CLEAR';
		}
		if (flight.refuelVolume) {
			consumption = flight.refuelVolume + ' L';
		}
		if (flight.bookings.list) {
			flight.bookings.list.forEach((b) => {
				const categories = [];
				b.passengers
					.map((p) => p.category)
					.filter(distinct)
					.sort((c1, c2) => +c1 - +c2)
					.forEach((cat) => {
						categories.push({
							category: cat,
							count: b.passengers.filter((p) => p.category === cat).length,
						});
					});
				const booking_passengers = [];
				b.passengers.forEach((p) => {
					booking_passengers.push({
						name: p.name,
						paymentType: this.getLabel('BOOKINGS.' + p.paymentType),
						paymentTypeEnum: p.paymentType,
						paymentReference: p.paymentReference,
						paymentAmount: p.paymentAmount,
						paymentStatus: this.getLabel('BOOKINGS.' + p.paymentStatus),
						paymentDate: p.paymentDate,
						category: p.category,
						voucher: p.voucher ? p.voucher.voucherReference : null,
						voucherNumber: p.voucherNumber,
						age: p.age,
						weight: p.weight,
						totalWeight: p.totalWeight,
						comments: p.comments,
					});
				});

				payments.push({
					contactName: b.contactName,
					contactPhone: b.contactPhone,
					contactEmail: b.contactEmail,
					totalAmount: b.passengerCounters.totalAmount,
					openAmount: b.passengerCounters.openAmount,
					paidAmount: b.passengerCounters.paidAmount,
					prepaymentAmount: b.prepaymentAmount,
					prepaymentDate: b.prepaymentDate,
					passengerCount: b.passengerCounters.passengerCount,
					passengers: booking_passengers,
					categories: categories,
					comments: b.comments,
					city: b.city,
					invoiceName: b.invoiceName,
					VATnumber: b.VATnumber,
				});
				if (b.passengers) {
					b.passengers.forEach((p) => {
						i += 1;
						passengers.push({
							booking: b.contactPhone,
							id: i,
							name: p.name,
							age: p.age,
							weight: p.weight,
							totalWeight: p.totalWeight,
							luggage: 1,
							child: p.child ? 1 : 0,
							disability: p.disability ? 1 : 0,
							transport: p.transport ? 1 : 0,
							signature: p.signature,
							category: p.category,
							comments: p.comments,
							onlineCheckinAt: b.onlineCheckinAt,
						});
					});
				}
			});
		}
		passengersCount = passengers.length;
		if (flight.pilot) {
			pic = flight.pilot.crew.name;
		}
		if (flight.groundCrews) {
			flight.groundCrews.forEach((c) => {
				crew += c.crew.name + ', ';
			});
		}
		const passengers_total_weight = flight.passengerCounters.passengersWeight;

		if (flight.hour) {
			flightHour = dayjs(flight.hour).format('HH:mm');
		}
		if (flight.landingTime) {
			landingTime = dayjs(flight.landingTime).format('HH:mm');
		}
		if (flight.takeoffTime) {
			takeoffTime = dayjs(flight.takeoffTime).format('HH:mm');
		}
		if (flight.windSummary && flight.windSummary !== '') {
			windSummary = flight.windSummary;
		} else {
			windSummary = `SURFACE: ${flight.windGlDirection}/${flight.windGl}KT`;
			windSummary = windSummary + '\n' + `1000FT: ${flight.wind10Direction}/${flight.wind10}KT`;
			windSummary = windSummary + '\n' + `2000FT: ${flight.wind20Direction}/${flight.wind20}KT`;
		}

		const flightPeriod = flight.period === 'EVE' ? ' PM' : ' AM';
		const flightDate = dayjs(flight.date).format('dd D-MM-YY') + flightPeriod;
		const flightDateShort = dayjs(flight.date).format('DD/MM/YYYY');
		const weatherDate = dayjs(flight.weatherForecastDate).format('D-MM-YYYY');
		manifestFlight = {
			data: {
				header: {
					type: 'Deprecated',
					docsn: 0, // TODO: flight.id,
					balloon: `${callSign} (${balloonName})`,
					balloonType: balloonType,
					pic: pic,
					crew: crew,
					pilot: flight.pilot,
					ground_crews: flight.groundCrews,
					pilotSignature: flight.pilotSignature,
					pilotComments: flight.pilotComments ? flight.pilotComments : '',
					date: flightDate,
					dateShort: flightDateShort,
					takeoffLocation: flight.location.name,
					takeoffAddress: flight.location.address + ', ' + flight.location.city,
					flightHour: flightHour,
					takeoffTime: takeoffTime,
					landingLocation: flight.landingLocation ? flight.landingLocation : '',
					landingTime: landingTime,
					flightComments: flight.comment,
					passengersCount,
				},
				weather: {
					source: flight.weatherSource,
					date: weatherDate,
					wind: `${flight.windGl} kt`,
					direction: `${flight.windGlDirection}°`,
					wind10: `${flight.wind10} kt - ${flight.wind10Direction}°`,
					wind20: `${flight.wind20} kt - ${flight.wind20Direction}°`,
					windSummary: windSummary,
					visual: visual,
					clouds: clouds,
					temp: `${flight.tempTakeoff} °C`,
					qfe: `${flight.weatherQnh} hPa`,
				},
				fuel: {
					plannedTime: `${flight.plannedFlightTime} min`,
					available: `${flight.maximumFlightTime} min`,
					needed: `${+flight.plannedFlightTime + 30} min`,
					consumption: consumption,
				},
				lift: {
					temp: `${flight.tempTakeoff} °C`,
					pressure: `${flight.weatherQnh} hPA`,
					takeoffElevation: `${flight.location.height} ft`,
					maxElevation: `${flight.maximumHeight} ft`,
					balloonTemp: `${flight.tempBalloon} °C`,
					volume: `${flight.balloon.envelopeVolume} m³`,
					lift: `${flight.liftAvailable} kg`,
					liftNeeded: `${flight.liftNeeded} kg`,
					envelope: `${flight.balloon.envelopeWeight} kg`,
					burner: `${flight.balloon.burnerWeight} kg`,
					basket: `${flight.balloon.basketWeight} kg`,
					cylinders: `${flight.balloon.balloonWeight.cylindersWeight} kg`,
					signature: flight.pilotSignature,
				},
				passengers: passengers,
				passengers_total_weight: passengers_total_weight + (flight.pilot ? flight.pilot.crew.totalWeight : 0),
				payments: payments,
			},
			labels: {
				header: {
					operator: 'OPERATOR',
					docsn: 'DOC S/N',
					balloon: this.getLabel('FLIGHTS.balloon'),
					balloonType: this.getLabel('FLIGHTS.type'),
					pic: 'PIC',
					crew: 'CREW',
					date: this.getLabel('FLIGHTS.date'),
					takeoffLocation: this.getLabel('FLIGHTS.takeoff_location'),
					takeoffAddress: this.getLabel('LOCATIONS.address'),
					takeoffTime: this.getLabel('FLIGHTS.takeoffTime'),
					landingLocation: this.getLabel('FLIGHTS.landingLocation'),
					landingTime: this.getLabel('FLIGHTS.landingTime'),
					load_chart_and_passenger_list: this.getLabel('MANIFEST.load_chart_and_passenger_list'),
					passenger_list: this.getLabel('MANIFEST.passenger_list'),
					weather_conditions: this.getLabel('MANIFEST.weather_conditions'),
					fuel_calculations: this.getLabel('MANIFEST.fuel_calculations'),
					load_chart: this.getLabel('MANIFEST.load_chart'),
					flightComments: this.getLabel('FLIGHTS.comment'),
				},
				weather: {
					source: this.getLabel('FLIGHTS.weather_source'),
					date: this.getLabel('FLIGHTS.weather_forecast_date'),
					wind: this.getLabel('FLIGHTS.wind_gl_short'),
					direction: this.getLabel('FLIGHTS.wind_dir'),
					wind10: this.getLabel('FLIGHTS.wind_10'),
					wind20: this.getLabel('FLIGHTS.wind_20'),
					windSummary: this.getLabel('FLIGHTS.windSummary'),
					visual: this.getLabel('FLIGHTS.vfr_visual'),
					clouds: this.getLabel('FLIGHTS.vfr_clouds'),
					temp: this.getLabel('FLIGHTS.temp'),
					qfe: 'QNH',
				},
				fuel: {
					plannedTime: this.getLabel('FLIGHTS.planned_flight_time'),
					available: this.getLabel('MANIFEST.fuel_available'),
					needed: this.getLabel('MANIFEST.fuel_needed'),
					consumption: this.getLabel('MANIFEST.fuel_consumption'),
				},
				lift: {
					temp: this.getLabel('FLIGHTS.temp_takeoff'),
					pressure: this.getLabel('FLIGHTS.weather_qfe'),
					takeoffElevation: this.getLabel('LOCATIONS.height'),
					maxElevation: this.getLabel('FLIGHTS.maximum_height'),
					balloonTemp: this.getLabel('FLIGHTS.balloon_temp'),
					volume: this.getLabel('BALLOONS.envelope_volume'),
					lift: this.getLabel('FLIGHTS.lift_available'),
					liftNeeded: this.getLabel('FLIGHTS.lift_needed'),
					envelope: this.getLabel('BALLOONS.envelope'),
					burner: this.getLabel('BALLOONS.burner'),
					basket: this.getLabel('BALLOONS.basket'),
					cylinders: this.getLabel('BALLOONS.cylinder'),
					signature: this.getLabel('FLIGHTS.signature'),
				},
				passengers: {
					name: this.getLabel('BOOKINGS.name'),
					weight: this.getLabel('BOOKINGS.weight'),
					luggage: this.getLabel('BOOKINGS.luggage'),
					child: this.getLabel('BOOKINGS.child'),
					disability: this.getLabel('BOOKINGS.disability'),
					signature: this.getLabel('BOOKINGS.signature'),
					acknowledge: this.translateCustom.translate('CHECKIN.acknowledge', this.translate.currentLang || this.translate.defaultLang || 'en'),
				},
				payments: {
					total_amount: this.getLabel('BOOKINGS.totalAmount'),
					open_amount: this.getLabel('BOOKINGS.openAmount'),
					paid_amount: this.getLabel('BOOKINGS.paid_amount'),
					paid_prepaymentamount: this.getLabel('BOOKINGS.prepayment'),
					prepayment_date: this.getLabel('BOOKINGS.prepaymentDate'),
					payment_type: this.getLabel('BOOKINGS.paymentType'),
					payment_reference: this.getLabel('BOOKINGS.paymentReference'),
					payment_amount: this.getLabel('BOOKINGS.paymentAmount'),
					payment_status: this.getLabel('BOOKINGS.paymentStatus'),
					payment_date: this.getLabel('BOOKINGS.paymentDate'),
					voucher: this.getLabel('VOUCHERS.voucher'),
				},
				footer: {
					confirmation: this.getLabel('MANIFEST.confirmation'),
					after_flight: this.getLabel('MANIFEST.after_flight'),
					visible_defects: this.getLabel('MANIFEST.visible_defects'),
					yes_no: this.getLabel('MANIFEST.yes_no'),
					date: this.getLabel('MANIFEST.date'),
					signature: this.getLabel('MANIFEST.signature'),
					defect: this.getLabel('MANIFEST.defect'),
					action: this.getLabel('MANIFEST.action'),
					certificate: this.getLabel('MANIFEST.certificate'),
					authority: this.getLabel('MANIFEST.authority'),
					initials: this.getLabel('MANIFEST.initials'),
				},
			},
		};
		return manifestFlight;
	}

	getLabel(value: any) {
		if (value) {
			return this.translateCustom.translate(value, 'en').toUpperCase();
		}
		return value;
	}
	capitalize(value: string) {
		if (value) {
			return value.charAt(0).toUpperCase() + value.slice(1);
		}
		return value;
	}

	async generatePaymentPDF(tenant, flight, save = true, display = true, currency = 'EUR') {
		if (tenant.toLowerCase() === 'culiair') {
			return this.generatePaymentPDFCuliAir(flight, save, display, currency);
		} else {
			return this.generatePaymentPDFAll(flight, save, display, currency);
		}
	}

	async generatePaymentPDFAll(flight, save = true, display = true, currency = 'EUR') {
		const document = this.mapFlightManifest(flight);
		const title = dayjs(flight.date).format('YYYYMMDD') + '_' + flight.period + '_' + document.data.header.balloon + '-payments.pdf';
		const doc = new jsPDF(),
			margins = {
				top: this.margin_top,
				left: this.margin_left,
			};
		autoTable(doc, {
			head: [[{ content: 'PAYMENTS', colSpan: 4 }]],
			body: [
				[document.data.header.balloon, document.data.header.date, document.data.header.pic, document.data.header.takeoffLocation],
				[
					{
						content: document.data.header.flightComments,
						colSpan: 4,
						styles: { fontSize: 9 },
					},
				],
			],
			startY: this.margin_top,
			theme: 'grid',
			margin: { left: this.margin_left },
			styles: {
				halign: 'left',
				valign: 'middle',
				fontSize: 14,
				cellPadding: 1,
			},
			tableWidth: 2 * this.column_width + this.margin_blocks,
			headStyles: {
				fillColor: '#d3d3d3',
				textColor: '#000000',
				fontSize: 14,
				halign: 'center',
			},
		});

		// Booking overview
		const categories = [];
		document.data.payments.forEach((p) => {
			const invoiceAndVat = `${p.invoiceName ? p.invoiceName : ''}\n${p.VATnumber ? p.VATnumber : ''}`;
			categories.push([
				{
					content: `${p.contactName}\n${p.contactPhone}`,
					styles: { fontStyle: 'bold', fontSize: 12 },
					colSpan: 2,
				},
				{
					content: p.passengerCount,
					styles: { halign: 'center', fontSize: 12 },
				},
				{
					content: p.prepaymentAmount ? currency + ' ' + p.prepaymentAmount : '',
					styles: {
						halign: 'center',
						fontSize: 12,
					},
				},
				{
					content: currency + ' ' + p.openAmount,
					styles: {
						fontSize: 12,
						fontStyle: 'bold',
						textColor: p.openAmount > 0 ? '#B50023' : '#555555',
						halign: 'center',
					},
				},
				{
					content: invoiceAndVat,
					styles: { fontStyle: 'bold', fontSize: 12 },
				},
				{
					content: currency + ' ' + p.totalAmount,
					styles: {
						fontSize: 12,
						fontStyle: 'bold',
						textColor: '#555555',
						halign: 'center',
					},
				},
			]);
			categories.push([{ content: p.comments ? p.comments : '', colSpan: 7 }]);
			p.passengers.forEach((pax, index) => {
				const paxName = pax.age ? pax.name + ' ' + `(${pax.age}y)` : pax.name;
				categories.push([
					{ content: index + 1 + '. ' + paxName, colSpan: 3 },
					{ content: pax.paymentType },
					{ content: currency + ' ' + (pax.paymentAmount ? pax.paymentAmount : 0), styles: { halign: 'center' } },
					{
						content:
							(pax.category ? 'Cat: ' + pax.category.name : '') +
							(pax.paymentTypeEnum === PaymentType.Coup ? ', Voucher: ' + pax.voucher : '') +
							(pax.paymentTypeEnum === PaymentType.Vouc ? ', Voucher: ' + pax.voucherNumber : ''),
						colSpan: 2,
					},
				]);
			});
		});
		const Ybetaaloverzicht = (doc as any).lastAutoTable.finalY;

		autoTable(doc, {
			head: [['BOOKING', '', '#', 'PREPAID', 'OPEN', 'INVOICE NAME & VAT', 'TOTAL']],
			body: categories,
			startY: Ybetaaloverzicht + 3,
			theme: 'grid',
			margin: { left: this.margin_left },
			styles: {
				halign: 'left',
				valign: 'middle',
				fontSize: 8,
				// cellPadding: 4,
				cellPadding: {
					top: 1,
					bottom: 1,
					left: 2,
					right: 2,
				},
			},
			tableWidth: 2 * this.column_width + this.margin_blocks,
			headStyles: {
				fillColor: '#d3d3d3',
				textColor: '#000000',
				fontSize: 12,
				halign: 'left',
			},
		});

		doc.setProperties({ title: title });
		if (display) window.open(doc.output('bloburl').toString());
		if (save) doc.save(title);
		return { title, pdf: doc.output('blob') };
	}

	async generatePaymentPDFCuliAir(flight, save = true, display = true, currency = 'EUR'): Promise<{ title: string; pdf: any }> {
		this.tenantSettingService.getTenantSettingMyFlight().valueChanges.subscribe((result) => {
			const logo = result.data.tenantSetting.operatorSettings.myflight.logo;
			const imgWidth = 40;
			const imgHeight = 30;

			const fillColor = result.data.tenantSetting.operatorSettings.myflight.colors[3];
			const textColor = result.data.tenantSetting.operatorSettings.myflight.colors[0];

			const document = this.mapFlightManifest(flight);
			const title = dayjs(flight.date).format('YYYYMMDD') + '_' + flight.period + '_' + document.data.header.balloon + '-passengerlist.pdf';
			const doc = new jsPDF(),
				margins = {
					top: this.margin_top,
					left: this.margin_left,
				};

			doc.addImage(logo, 'jpg', 150, 15, imgWidth, imgHeight);
			autoTable(doc, {
				head: [[{ content: 'VAART', colSpan: 3 }]],
				body: [
					[{ content: document.data.header.balloon, colSpan: 3 }],
					[document.data.header.takeoffLocation, document.data.header.date, document.data.header.flightHour],
					[
						{
							content: document.data.header.flightComments,
							colSpan: 3,
							styles: { fontSize: 9 },
						},
					],
				],
				startY: this.margin_top + 30,
				theme: 'grid',
				margin: { left: this.margin_left },
				styles: {
					halign: 'left',
					valign: 'middle',
					fontSize: 14,
					cellPadding: 1,
				},
				tableWidth: 2 * this.column_width + this.margin_blocks,
				headStyles: {
					fillColor,
					textColor,
					fontSize: 14,
					halign: 'center',
				},
			});

			// Booking overview
			const categories = [];
			document.data.payments.forEach((p) => {
				categories.push([
					{ content: p.contactName, styles: { fontStyle: 'bold', fontSize: 12 }, colSpan: 2 },
					{ content: p.contactPhone, styles: { fontSize: 12 } },
					{ content: p.contactEmail, styles: { fontSize: 12 } },
				]);
				categories.push([{ content: p.comments ? p.comments : '', colSpan: 4 }]);
				p.passengers.forEach((pax, index) => {
					const paxAge = pax.age ? `${pax.age} y` : '';
					const comments = pax.comments;
					categories.push([
						{ content: index + 1 + '. ' + pax.name },
						{ content: paxAge },
						{ content: `${pax.weight} kg` },
						{ content: comments ?? '' },
					]);
				});
			});
			// add pic from data.header.pic as last line
			// categories.push([
			// 	{ content: document.data.header.pilot.crew.name, styles: { fontStyle: 'bold', fontSize: 12 }, colSpan: 2 },
			// 	{ content: document.data.header.pilot.crew.phone, styles: { fontSize: 12 }, },
			// 	{ content: document.data.header.pilot.crew.email, styles: { fontSize: 12, }, },

			// ]);
			// categories.push([
			// 	{ content: document.data.header.pilot.crew.name, colSpan: 4 },
			// ]);
			// crew each of them as line
			document.data.header.ground_crews.forEach((g) => {
				categories.push([{ content: g.crew.name, colSpan: 4 }]);
			});
			const Ybetaaloverzicht = (doc as any).lastAutoTable.finalY;

			autoTable(doc, {
				head: [['RESERVATIE', '', 'TELEFOON', 'EMAIL']],
				body: categories,
				startY: Ybetaaloverzicht + 3,
				theme: 'grid',
				margin: { left: this.margin_left },
				styles: {
					halign: 'left',
					valign: 'middle',
					fontSize: 8,
					// cellPadding: 4,
					cellPadding: {
						top: 1,
						bottom: 1,
						left: 2,
						right: 2,
					},
				},
				tableWidth: 2 * this.column_width + this.margin_blocks,
				headStyles: {
					fillColor: '#FADECB',
					textColor: '#EC660B',
					fontSize: 12,
					halign: 'left',
				},
			});

			doc.setProperties({ title: title });
			if (display) window.open(doc.output('bloburl').toString());
			if (save) doc.save(title);
			return { title, pdf: doc.output('blob') };
		});
		return { title: '', pdf: null };
	}

	// passenger checkin PDF — with check-in instructions (online check-in enabled) every tenant gets the
	// online signature sheet; without instructions the legacy sheet is used.
	generateCheckinPDF(flight, checkinInstructions = '', save = true, display = true, operatorName = '') {
		if (!checkinInstructions) {
			return this.generateCheckinPDFBallooning(flight, save, display);
		}
		return this.generateCheckinPDFOnline(flight, checkinInstructions, save, display, operatorName);
	}

	// modernised online check-in signature sheet — see specs/features/023-passenger-online-checkin
	generateCheckinPDFOnline(flight: any, checkinInstructions = '', save = true, display = true, operatorName = '') {
		const document = this.mapFlightManifest(flight);
		const lang = this.translate.currentLang || this.translate.defaultLang || 'en';
		const t = (key: string) => this.translateCustom.translate(`CHECKIN.${key}`, lang) ?? key;

		const balloonModel: string = flight.balloon?.name ?? '';
		const balloonRegistration: string = flight.balloon?.callSign ?? '';
		const flightCode = `${dayjs(flight.date).format('YYYYMMDD')}-${flight.period}-${(balloonModel || '').slice(0, 6).toUpperCase()}`;
		const title = `${dayjs(flight.date).format('YYYYMMDD')}_${flight.period}_${balloonModel || balloonRegistration}-checkin.pdf`;

		// dayjs only has a 'nl' locale — Belgian (nl) and Netherlands (du) Dutch both use it
		const dayjsLocale = lang === 'du' ? 'nl' : lang;
		const formattedDate = dayjs(flight.date).locale(dayjsLocale).format('dd D MMM YY');

		const doc = new jsPDF({ unit: 'mm', format: 'a4', compress: true });

		// 1 — Brand band
		this.drawCheckinBrandBand(doc, operatorName, flightCode, t);

		// 2 — Flight info card
		const cardBottom = this.drawCheckinFlightCard(doc, 22, {
			date: this.capitalize(formattedDate),
			period: this.checkinPeriodLabel(flight.period, t),
			balloonModel,
			balloonRegistration,
			pic: document.data.header.pic,
			takeoffLocation: document.data.header.takeoffLocation,
			takeoffTime: document.data.header.takeoffTime,
		}, t);

		// 3 — Safety briefing (the agreement text the passengers acknowledge)
		const briefingTopY = this.drawCheckinSectionLabel(doc, cardBottom + 8, t('pdf_safety_briefing'), t('pdf_please_read'));
		const briefingBottom = this.drawCheckinInstructions(doc, briefingTopY + 1, this.parseCheckinInstructions(checkinInstructions));

		// 4 — Acknowledgement callout — the exact CHECKIN.acknowledge statement, in bold, directly below the
		// agreement text and above the signature area (spec 023 §6)
		const maxY = 297 - 14;
		let ackTop = briefingBottom + 4;
		if (ackTop + 22 > maxY) {
			doc.addPage();
			ackTop = 14;
		}
		this.drawCheckinAcknowledgement(doc, ackTop, t('acknowledge'), t);

		// 5 — Passenger manifest (signature area) — always on its own page: instructions
		// page(s) first, the signature list starts on a fresh page
		const bookings = (flight?.bookings?.list ?? []) as any[];
		const totalPax = bookings.reduce((n: number, b: any) => n + (b.passengers?.length ?? 0), 0);
		const subtitle = (t('pdf_manifest_subtitle') as string)
			.replace('{{pax}}', String(totalPax))
			.replace('{{bookings}}', String(bookings.length));
		doc.addPage();
		const manifestTopY = this.drawCheckinSectionLabel(doc, 14, t('pdf_passenger_manifest'), subtitle);
		this.drawCheckinPassengerTable(doc, manifestTopY, flight, t);

		// 6 — Footer on every page (now that total page count is known)
		const pageCount = doc.getNumberOfPages();
		for (let p = 1; p <= pageCount; p++) {
			doc.setPage(p);
			this.drawCheckinFooter(doc, t, p, pageCount);
		}

		doc.setProperties({ title });
		if (display) window.open(doc.output('bloburl').toString());
		if (save) doc.save(title);
		return { title, pdf: doc.output('blob') };
	}

	// legacy passenger checkin sheet (pre-dates online check-in) — used when no check-in instructions are configured
	generateCheckinPDFBallooning(flight, save = true, display = true) {
		const document = this.mapFlightManifest(flight);
		const title = dayjs(flight.date).format('YYYYMMDD') + '_' + flight.period + '_' + document.data.header.balloon + '-checkin.pdf';
		const doc = new jsPDF();
		autoTable(doc, {
			head: [[{ content: 'CHECK-IN', colSpan: 4 }]],
			body: [
				[document.data.header.balloon, document.data.header.date, document.data.header.pic, document.data.header.takeoffLocation],
				[
					{
						content: document.data.header.flightComments,
						colSpan: 4,
						styles: { fontSize: 9 },
					},
				],
			],
			startY: this.margin_top,
			theme: 'grid',
			margin: { left: this.margin_left },
			styles: {
				halign: 'left',
				valign: 'middle',
				fontSize: 14,
				cellPadding: 1,
				textColor: '#000000',
			},
			tableWidth: 2 * this.column_width + this.margin_blocks,
			headStyles: {
				fillColor: '#d3d3d3',
				textColor: '#000000',
				fontSize: 14,
				halign: 'center',
			},
		});

		// Booking overview
		const passengers = [];
		document.data.passengers.forEach((pax, index) => {
			const paxName = pax.age ? pax.name + ' ' + `(${pax.age}y)` : pax.name;
			const paxWeight = pax.weight ? pax.weight : '';
			passengers.push([{ content: index + 1 + '. ' + paxName }, { content: paxWeight }, { content: '' }]);
		});

		const Ybetaaloverzicht = (doc as any).lastAutoTable.finalY;

		autoTable(doc, {
			head: [['BOOKING', 'WEIGHT', 'SIGNATURE']],
			body: passengers,
			startY: Ybetaaloverzicht + 3,
			theme: 'grid',
			margin: { left: this.margin_left },
			styles: {
				halign: 'left',
				valign: 'middle',
				fontSize: 12,
				textColor: '#000000',
				cellPadding: {
					top: 1,
					bottom: 1,
					left: 2,
					right: 2,
				},
				minCellHeight: 8,
			},
			tableWidth: 2 * this.column_width + this.margin_blocks,
			headStyles: {
				fillColor: '#d3d3d3',
				textColor: '#000000',
				fontSize: 12,
				halign: 'left',
			},
		});

		const Yfooter = 260;

		const footerText1 = 'Door deze lijst af te tekenen geeft de passagier aan dat';
		const footerText2 = '- hij een veiligheidsbriefing gekregen heeft van de piloot';
		const footerText3 = '- hij kennis neemt van de voorwaarden die aan de ommezijde van dit formulier staan om mee te ballonvaren';
		const footerText4 = '- en deze aanvaardt';
		// Footer
		doc.setFontSize(10);
		doc.text(footerText1, this.margin_left, Yfooter + 10);
		doc.text(footerText2, this.margin_left, Yfooter + 14);
		doc.text(footerText3, this.margin_left, Yfooter + 18);
		doc.text(footerText4, this.margin_left, Yfooter + 22);

		doc.setProperties({ title: title });
		if (display) window.open(doc.output('bloburl').toString());
		if (save) doc.save(title);
		return { title, pdf: doc.output('blob') };
	}

	// ── Checkin PDF drawing helpers ──────────────────────────────────────────

	private parseCheckinInstructions(input: string): any[] {
		if (!input) return [];
		// Operator settings editor outputs HTML; legacy callers may still send Markdown.
		if (/<\/?[a-z][\s\S]*?>/i.test(input)) {
			return this.parseCheckinInstructionsHtml(input);
		}
		const blocks: any[] = [];
		let buffer: string[] = [];
		let inList: { kind: 'ul'; items: string[] } | null = null;
		const flushP = () => {
			if (!buffer.length) return;
			blocks.push({ kind: 'p', text: buffer.join(' ').replace(/\s+/g, ' ').trim() });
			buffer = [];
		};
		const flushL = () => { if (inList) { blocks.push(inList); inList = null; } };
		for (const raw of input.split('\n')) {
			const line = raw.trim();
			if (!line) { flushP(); flushL(); continue; }
			const h = line.match(/^(#{2,3})\s+(.*)$/);
			if (h) { flushP(); flushL(); blocks.push({ kind: h[1].length === 2 ? 'h2' : 'h3', text: h[2] }); continue; }
			const b = line.match(/^[-*]\s+(.*)$/);
			if (b) { flushP(); if (!inList) inList = { kind: 'ul', items: [] }; inList.items.push(b[1]); continue; }
			flushL();
			buffer.push(line.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1').replace(/`([^`]+)`/g, '$1'));
		}
		flushP(); flushL();
		return blocks;
	}

	private parseCheckinInstructionsHtml(html: string): any[] {
		const wrap = window.document.createElement('div');
		wrap.innerHTML = html;
		const blocks: any[] = [];
		const cleanText = (el: Element | null) => (el?.textContent || '').replace(/\s+/g, ' ').trim();
		const visit = (el: Element) => {
			const tag = el.tagName.toLowerCase();
			if (tag === 'h1' || tag === 'h2') {
				const text = cleanText(el);
				if (text) blocks.push({ kind: 'h2', text });
			} else if (tag === 'h3' || tag === 'h4' || tag === 'h5' || tag === 'h6') {
				const text = cleanText(el);
				if (text) blocks.push({ kind: 'h3', text });
			} else if (tag === 'p' || tag === 'blockquote') {
				const text = cleanText(el);
				if (text) blocks.push({ kind: 'p', text });
			} else if (tag === 'ul' || tag === 'ol') {
				const items: string[] = [];
				Array.from(el.children).forEach((child) => {
					if (child.tagName.toLowerCase() === 'li') {
						const t = cleanText(child);
						if (t) items.push(t);
					}
				});
				if (items.length) blocks.push({ kind: 'ul', items });
			} else if (tag === 'br' || tag === 'hr') {
				return;
			} else {
				// wrapper: recurse into children
				Array.from(el.children).forEach(visit);
			}
		};
		Array.from(wrap.children).forEach(visit);
		// Fallback: if the HTML had no recognised block tags, push the plain text as a paragraph
		if (blocks.length === 0) {
			const text = cleanText(wrap);
			if (text) blocks.push({ kind: 'p', text });
		}
		return blocks;
	}

	private drawCheckinBrandBand(doc: jsPDF, operatorName: string, flightCode: string, t: (k: string) => string): void {
		const h = 18;
		doc.setFillColor('#1E3A5F');
		doc.rect(0, 0, 210, h, 'F');
		doc.setTextColor('#FFFFFF');
		doc.setFont('helvetica', 'bold');
		doc.setFontSize(11);
		doc.text((operatorName || '').toUpperCase(), 15, h / 2 - 1.4, { baseline: 'middle' });
		doc.setFont('helvetica', 'normal');
		doc.setFontSize(7.5);
		doc.setTextColor('#B6C4D6');
		doc.text(t('pdf_balloon_operator'), 15, h / 2 + 2.6, { baseline: 'middle' });
		doc.setTextColor('#FFFFFF');
		doc.setFont('helvetica', 'bold');
		doc.setFontSize(16);
		doc.text(t('pdf_check_in'), 195, h / 2 - 1.6, { baseline: 'middle', align: 'right' });
		doc.setFont('helvetica', 'normal');
		doc.setFontSize(7.5);
		doc.setTextColor('#B6C4D6');
		doc.text(`${t('pdf_passenger_manifest')} · ${flightCode}`, 195, h / 2 + 2.6, { baseline: 'middle', align: 'right' });
	}

	private drawCheckinFlightCard(
		doc: jsPDF,
		y: number,
		f: { date: string; period: string; balloonModel: string; balloonRegistration: string; pic: string; takeoffLocation?: string; takeoffTime?: string },
		t: (k: string) => string,
	): number {
		const cardH = 26;
		doc.setFillColor('#FFFFFF');
		doc.setDrawColor('#E2E8F0');
		doc.setLineWidth(0.3);
		doc.roundedRect(15, y, 180, cardH, 1.5, 1.5, 'FD');
		const balloonSub = f.balloonRegistration || '';
		const fields = [
			{ label: t('pdf_date'), value: f.date, sub: f.period },
			{ label: t('pdf_balloon'), value: f.balloonModel || '—', sub: balloonSub },
			{ label: t('pdf_pic'), value: f.pic, sub: '' },
			{ label: t('pdf_takeoff'), value: f.takeoffLocation ?? '—', sub: f.takeoffTime ? `${t('pdf_local_time')} ${f.takeoffTime}` : '' },
		];
		const colW = 180 / 4;
		fields.forEach((field, i) => {
			const cx = 15 + i * colW + 4;
			if (i > 0) { doc.setDrawColor('#E2E8F0'); doc.line(15 + i * colW, y + 5, 15 + i * colW, y + cardH - 5); }
			doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor('#475569');
			doc.text(field.label, cx, y + 7);
			doc.setFont('helvetica', 'bold'); doc.setFontSize(11.5); doc.setTextColor('#0F172A');
			doc.text(field.value, cx, y + 14);
			doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor('#94A3B8');
			if (field.sub) doc.text(field.sub, cx, y + 20);
		});
		return y + cardH;
	}

	private drawCheckinSectionLabel(doc: jsPDF, y: number, label: string, rightLabel?: string): number {
		doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor('#1E3A5F');
		doc.text(label.toUpperCase(), 15, y);
		if (rightLabel) { doc.setFont('helvetica', 'normal'); doc.setTextColor('#475569'); doc.text(rightLabel, 195, y, { align: 'right' }); }
		doc.setDrawColor('#E2E8F0'); doc.setLineWidth(0.3);
		doc.line(15, y + 1.5, 195, y + 1.5);
		return y + 5;
	}

	private drawCheckinPassengerTable(doc: jsPDF, y: number, flight: any, t: (k: string) => string): number {
		const passengers: { seat: number | string; name: string; contact: string; bookingIdx: number }[] = [];
		((flight?.bookings?.list ?? []) as any[]).forEach((booking: any, bIdx: number) => {
			(booking.passengers ?? []).forEach((p: any) => {
				// booking contact + phone as subtitle; just the phone when the passenger is the contact
				const isContact = (booking.contactName ?? '').trim() === (p.name ?? '').trim();
				const contact = [isContact ? '' : booking.contactName, booking.contactPhone].filter(Boolean).join(' · ');
				passengers.push({ seat: passengers.length + 1, name: p.age ? `${p.name} (${p.age}y)` : p.name, contact, bookingIdx: bIdx });
			});
		});
		const minRows = Math.max(passengers.length + 2, 8);
		while (passengers.length < minRows) passengers.push({ seat: '', name: '', contact: '', bookingIdx: -1 });

		const half = Math.ceil(passengers.length / 2);
		const left = passengers.slice(0, half);
		const right = passengers.slice(half);
		const body: any[] = [];
		for (let i = 0; i < Math.max(left.length, right.length); i++) {
			const L = left[i] ?? { seat: '', name: '', contact: '', bookingIdx: -1 };
			const R = right[i] ?? { seat: '', name: '', contact: '', bookingIdx: -1 };
			const lFill = L.bookingIdx >= 0 && L.bookingIdx % 2 === 1 ? '#F4F7FB' : '#FFFFFF';
			const rFill = R.bookingIdx >= 0 && R.bookingIdx % 2 === 1 ? '#F4F7FB' : '#FFFFFF';
			body.push([
				{ content: L.seat ? String(L.seat) : '', styles: { fillColor: lFill, textColor: '#94A3B8', halign: 'center', fontSize: 10 } },
				{ content: L.name, contact: L.contact, styles: { fillColor: lFill, textColor: L.name ? '#0F172A' : '#94A3B8', fontStyle: L.name ? 'bold' : 'normal', valign: L.contact ? 'top' : 'middle' } },
				{ content: '', styles: { fillColor: lFill } },
				{ content: '', styles: { fillColor: '#FFFFFF', cellPadding: 0, lineWidth: 0 } },
				{ content: R.seat ? String(R.seat) : '', styles: { fillColor: rFill, textColor: '#94A3B8', halign: 'center', fontSize: 10 } },
				{ content: R.name, contact: R.contact, styles: { fillColor: rFill, textColor: R.name ? '#0F172A' : '#94A3B8', fontStyle: R.name ? 'bold' : 'normal', valign: R.contact ? 'top' : 'middle' } },
				{ content: '', styles: { fillColor: rFill } },
			]);
		}
		autoTable(doc, {
			head: [[
				{ content: '#', styles: { halign: 'center' } },
				{ content: t('pdf_passenger'), styles: { halign: 'left' } },
				{ content: t('pdf_signature'), styles: { halign: 'left' } },
				{ content: '', styles: { fillColor: '#FFFFFF', lineWidth: 0 } },
				{ content: '#', styles: { halign: 'center' } },
				{ content: t('pdf_passenger'), styles: { halign: 'left' } },
				{ content: t('pdf_signature'), styles: { halign: 'left' } },
			]],
			body,
			startY: y,
			theme: 'plain',
			margin: { left: 15, right: 15 },
			tableWidth: 180,
			styles: {
				font: 'helvetica',
				fontSize: 10,
				textColor: '#0F172A',
				cellPadding: { top: 2.6, bottom: 2.6, left: 3, right: 3 },
				lineColor: '#E2E8F0',
				lineWidth: 0.2,
				minCellHeight: 11,
				valign: 'middle',
			},
			columnStyles: {
				0: { cellWidth: 11 }, 1: { cellWidth: 36 }, 2: { cellWidth: 39 },
				3: { cellWidth: 4 },
				4: { cellWidth: 11 }, 5: { cellWidth: 36 }, 6: { cellWidth: 43 },
			},
			headStyles: {
				fillColor: '#E8EEF5',
				textColor: '#1E3A5F',
				fontStyle: 'bold',
				fontSize: 8,
				cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
				lineColor: '#E2E8F0',
				lineWidth: 0.2,
			},
			didParseCell: (data: CellHookData) => {
				if (data.column.index === 3) { data.cell.styles.lineWidth = 0; data.cell.styles.fillColor = '#FFFFFF'; }
			},
			didDrawCell: (data: CellHookData) => {
				if (data.section !== 'body') return;
				// booking contact subtitle under the passenger name
				if (data.column.index === 1 || data.column.index === 5) {
					const contact = (data.cell.raw as any)?.contact;
					if (contact) {
						doc.setFont('helvetica', 'italic');
						doc.setFontSize(7);
						doc.setTextColor('#94A3B8');
						doc.text(contact, data.cell.x + 3, data.cell.y + data.cell.height - 2.6);
					}
					return;
				}
				if (data.column.index !== 2 && data.column.index !== 6) return;
				const { x, y: cy, width, height } = data.cell;
				const lineY = cy + height - 3;
				doc.setDrawColor('#94A3B8');
				doc.setLineWidth(0.2);
				(doc as any).setLineDashPattern([0.6, 0.8], 0);
				doc.line(x + 2, lineY, x + width - 2, lineY);
				(doc as any).setLineDashPattern([], 0);
				doc.setFont('helvetica', 'normal');
				doc.setFontSize(8);
				doc.setTextColor('#94A3B8');
				doc.text('×', x + 2, lineY - 0.4);
			},
		});
		return (doc as any).lastAutoTable.finalY;
	}

	private drawCheckinInstructions(doc: jsPDF, y: number, blocks: any[]): number {
		const x = 15;
		const w = 180;
		const maxY = 297 - 14;          // bottom safe area (above footer band)
		const pageTopY = 14;            // top margin on continuation pages
		let cy = y;

		const ensureSpace = (needed: number) => {
			if (cy + needed > maxY) {
				doc.addPage();
				cy = pageTopY;
			}
		};

		for (const block of blocks) {
			if (block.kind === 'h2') {
				ensureSpace(8);
				doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor('#1E3A5F');
				doc.text(block.text, x, cy + 3);
				cy += 4 + 2.4;
			} else if (block.kind === 'h3') {
				ensureSpace(6);
				doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor('#0F172A');
				doc.text(block.text, x, cy + 3);
				cy += 3.6 + 1.6;
			} else if (block.kind === 'p') {
				doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor('#475569');
				const lines = doc.splitTextToSize(block.text, w);
				// Render line-by-line so a long paragraph can break mid-page
				for (const line of lines) {
					ensureSpace(3.6);
					doc.text(line, x, cy + 3);
					cy += 3.6;
				}
				cy += 2;
			} else if (block.kind === 'ul') {
				doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
				for (const item of block.items) {
					const lines = doc.splitTextToSize(item, w - 4);
					// Keep bullet + first line together; subsequent wrapped lines can break
					ensureSpace(3.6);
					doc.setTextColor('#1E3A5F'); doc.text('•', x, cy + 3);
					doc.setTextColor('#475569'); doc.text(lines[0], x + 3, cy + 3);
					cy += 3.6;
					for (let i = 1; i < lines.length; i++) {
						ensureSpace(3.6);
						doc.text(lines[i], x + 3, cy + 3);
						cy += 3.6;
					}
				}
				cy += 2;
			}
		}
		return cy;
	}

	private drawCheckinAcknowledgement(doc: jsPDF, y: number, statement: string, t: (k: string) => string): number {
		const h = 18;
		doc.setFillColor('#E8EEF5');
		doc.setDrawColor('#1E3A5F');
		doc.setLineWidth(0.4);
		doc.roundedRect(15, y, 180, h, 1.5, 1.5, 'FD');
		doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor('#1E3A5F');
		doc.text(t('pdf_acknowledgement'), 19, y + 5);
		doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor('#0F172A');
		const lines = doc.splitTextToSize(statement, 172);
		doc.text(lines, 19, y + 9);
		return y + h;
	}

	private drawCheckinFooter(doc: jsPDF, t: (k: string) => string, current: number, total: number): void {
		const y = 297 - 9;
		doc.setDrawColor('#E2E8F0'); doc.setLineWidth(0.2);
		doc.line(15, y - 4, 195, y - 4);
		doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor('#94A3B8');
		doc.text(dayjs().format('YYYY-MM-DD'), 15, y);
		const pageText = (t('pdf_page') as string).replace('{{current}}', String(current)).replace('{{total}}', String(total));
		doc.text(pageText, 195, y, { align: 'right' });
	}

	private checkinPeriodLabel(period: string, t: (k: string) => string): string {
		switch ((period || '').toUpperCase()) {
			case 'AM': case 'MORNING': return t('pdf_morning_flight');
			case 'PM': case 'EVENING': case 'EVE': return t('pdf_evening_flight');
			default: return period || '';
		}
	}

}
